/**
 * App Context Factory
 * Creates unified context for web and mobile with shared dependencies.
 *
 * Chat is local-only: bots carry user-supplied API keys and the LLM is invoked
 * directly (web/mobile) or through the LLM proxy worker for CORS bypass.
 * Sessions live entirely in the local sandbox.
 */

import createLlmSdk from 'llm-sdk'
import { buildBotSystemPrompt } from 'myagent_core'
import { createBotConfigService, createPeerService } from 'peer-sdk'
import { createSessionManager } from 'session-manager'
import { createFrontendClient, forwardCall, forwardCallByName } from 'workers-common'
import { AuthManager } from '../auth/index.js'
import { createApiService } from '../services/index.js'
import { Store } from '../utils/index.js'

// Decode JWT sub for consistent userId across sandbox calls
const withLocalAuth =
  (fn: any) =>
  async (ctx: any, ...args: any[]) => {
    const authHeader = ctx.headers?.authorization
    if (authHeader?.startsWith('Bearer ')) {
      try {
        const sub = JSON.parse(atob(authHeader.split('.')[1])).sub
        if (sub) ctx.auth_info = { sub }
      } catch {}
    }
    return fn(ctx, ...args)
  }

export function createAppContext({
  storageAdapter,
  getPrivateKey,
  store,
  httpClient,
  // Optional DI overrides for e2e / test environments. Each override returning
  // undefined falls through to the real implementation. None of these are read
  // from `window` here — the platform entry (web/mobile/cli) is responsible for
  // wrapping window-level mocks before passing them in.
  chatOverride,
  roleCallOverride,
  userWorkerRegistryListOverride,
  seedFromLocalStorage,
}: any) {
  // Initialize auth manager
  const auth = new AuthManager(storageAdapter)

  // Initialize local storage
  const appStore = store || new Store(storageAdapter)

  // Local session sandbox
  const sessionManager = createSessionManager(appStore)
  const sessionSandbox = Object.fromEntries(
    Object.entries(sessionManager.sandbox).map(([k, fn]) => [`session.${k}`, withLocalAuth(fn)]),
  )

  // Initialize client (singleton)
  let clientInstance: any = null

  const getClient = () => {
    if (!clientInstance) {
      clientInstance = createFrontendClient({
        getPrivateKey,
        getAccessToken: async () => await auth.getValidAccessToken(),
        sandbox: sessionSandbox,
      })
      auth.setClient(clientInstance)
    }
    return clientInstance
  }

  // Build ctx with authorization header for downstream calls
  const buildCtx = async () => {
    const token = await auth.getValidAccessToken()

    return {
      headers: {
        authorization: token ? `Bearer ${token}` : undefined,
      },
    }
  }

  // Create bot config service (custom LLM bot management)
  const botConfig = createBotConfigService(storageAdapter, {
    storageKey: 'myagent_bots',
    seedFromLocalStorage: !!seedFromLocalStorage,
  })

  // Create frontend LLM SDK factory (for mobile direct mode — requires httpClient)
  let frontendLlmSdk: any = null
  const getFrontendLlmSdk = httpClient
    ? () => {
        if (!frontendLlmSdk) frontendLlmSdk = createLlmSdk({ httpClient })
        return frontendLlmSdk
      }
    : null

  // Create API service — sessions go through 'local' sandbox; chat goes through llm.proxy worker
  const api = createApiService({
    workerName: 'local',
    getClient,
    getFrontendLlmSdk,
    chatOverride,
  })

  // Discover remote roles via user-worker-registry (cached with 30s TTL)
  let rolesCache: any[] | null = null
  let rolesCacheTime = 0
  const ROLES_CACHE_TTL = 30000

  const discoverRoles = async ({ forceRefresh = false } = {}) => {
    const now = Date.now()
    if (!forceRefresh && rolesCache && now - rolesCacheTime < ROLES_CACHE_TTL) return rolesCache

    let result: { bindings?: any[] } | undefined
    if (userWorkerRegistryListOverride) {
      const overridden = await userWorkerRegistryListOverride()
      if (overridden !== undefined) result = overridden
    }

    if (!result) {
      const ctx = await buildCtx()
      if (!ctx.headers?.authorization) return rolesCache || []

      const client = getClient()
      result = (await forwardCallByName(ctx, client, 'user-worker-registry', 'list', [{}])) as {
        bindings?: any[]
      }
    }

    const isHex = (s: any) => s && s.length >= 10 && /^[0-9a-f]+$/i.test(s)
    const roles = (result?.bindings || []).map((w: any) => ({
      roleId: w.workerPublicKey,
      workerType: w.workerType,
      roleType: w.metadata?.roleType || 'tool',
      displayName:
        w.metadata?.name ||
        (w.deviceName && !isHex(w.deviceName) ? w.deviceName : null) ||
        w.workerType,
      typeName: w.metadata?.name || w.workerType,
      description: w.metadata?.description || '',
      online: !!w.online,
    }))

    rolesCache = roles
    rolesCacheTime = now
    return roles
  }

  // Direct RPC call to a role by publicKey
  const roleCall = async (roleId: string, method: string, params: any[]) => {
    if (roleCallOverride) {
      const overridden = await roleCallOverride(roleId, method, params)
      if (overridden !== undefined) return overridden
    }
    const ctx = await buildCtx()
    return forwardCall(ctx, getClient(), roleId, method, params)
  }

  // Delete a role binding from user-worker-registry
  const deleteRole = async (roleId: string) => {
    const ctx = await buildCtx()
    await forwardCallByName(ctx, getClient(), 'user-worker-registry', 'unbind', [
      { workerPublicKey: roleId },
    ])
    rolesCache = null
  }

  // Create peer service (unified peer interactions + chat engine)
  const peerService = createPeerService({
    botConfigService: botConfig,
    discoverRoles,
    roleCall,
    deleteRole,
    chatFn: (messages: any, botCfg: any) => api.chat({ messages }, botCfg),
    buildPrompt: buildBotSystemPrompt,
  })

  return {
    auth,
    getClient,
    api,
    botConfig,
    peerService,
    roleCall,
    buildCtx,
  }
}
